import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Avatar } from "@mui/material";
import dayjs from "dayjs";
import BaseCard from "../../../components/ui/BaseCard";
import BaseButton from "../../../components/ui/BaseButton";
import BaseDataTableV2 from "../../../components/ui/BaseDataTableV2";
import BaseTextField from "../../../components/ui/BaseTextField";
import BaseTooltip from "../../../components/ui/BaseTooltip";
import { fetchChildClients } from "../../../services/api/client";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import useAuth from "../../../hooks/useAuth";
import { debounce, formatPhoneNumber } from "../../../utils/helpers";
import { joinNonEmpty } from "../../../utils/displayValue";
import CompleteProfileModal from "../../WorkJobDetails/CompleteProfileModal";

const EntitiesTable = () => {
  const { auth, authRole } = useAuth();
  const { userData } = useCurrentUser();
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({ page: 0, pageSize: 10 });
  const [search, setSearch] = useState("");
  const [isProfileCompleted, setIsProfileCompleted] = useState(false);

  const customerId = auth?.user?.customer?.id ?? userData?.customerId;
  // Sub-users see the entities but only the owner adds one (the API checks too).
  const isOwner = auth?.user?.isCustomerOwner ?? userData?.isCustomerOwner;

  const queryParams = {
    page: pagination.page + 1,
    limit: pagination.pageSize,
    search,
  };

  const { isPending, data } = useQuery({
    queryKey: ["customerEntities", customerId, queryParams],
    queryFn: () => fetchChildClients(customerId, queryParams),
    enabled: !!customerId,
    placeholderData: (prev) => prev,
  });

  const entities = data?.data?.data || [];
  const totalCount = data?.data?.totalCount || 0;

  const debouncedSearchRef = useRef(
    debounce((value) => {
      setSearch(value);
      setPagination((p) => ({ ...p, page: 0 }));
    }, 500),
  );

  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.5,
      renderCell: (params) => (
        <p className="table-font-size font-normal text-primary ml-2 cursor-pointer">
          {params.row.id}
        </p>
      ),
    },
    {
      field: "clientName",
      headerName: "Name",
      flex: 1.5,
      renderCell: (params) => (
        <div className="flex items-center gap-3 cursor-pointer">
          <Avatar sx={{ width: 28, height: 28, fontSize: 12 }} src={params.row.clientLogo}>
            {params.row.clientName?.[0]}
          </Avatar>
          <p className="table-font-size font-normal capitalize text-primary">
            {params.row.clientName}
          </p>
        </div>
      ),
    },
    {
      field: "address",
      headerName: "Address",
      flex: 1.5,
      renderCell: (params) => {
        const address = params.row.address;
        const fullAddress = joinNonEmpty([
          address?.addressLine1,
          address?.city,
          address?.state,
          address?.zipCode,
          address?.country,
        ]);

        if (!fullAddress) return <p className="table-font-size">-</p>;

        return (
          <BaseTooltip placement="top" content={<p className="text-white">{fullAddress}</p>}>
            <p className="table-font-size truncate overflow-hidden whitespace-nowrap">
              {fullAddress}
            </p>
          </BaseTooltip>
        );
      },
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.5,
      renderCell: (params) => <p className="table-font-size">{params.row.email || "-"}</p>,
    },
    {
      field: "phoneNumber",
      headerName: "Mobile",
      flex: 1.2,
      renderCell: (params) => (
        <p className="table-font-size">{formatPhoneNumber(params.row.phoneNumber) || "-"}</p>
      ),
    },
    {
      field: "managerName",
      headerName: "Contact Person",
      flex: 1.2,
      renderCell: (params) => (
        <p className="table-font-size capitalize">{params.row.managerName || "-"}</p>
      ),
    },
    {
      field: "createdAt",
      headerName: "Date",
      flex: 1,
      renderCell: (params) => (
        <p className="table-font-size">{dayjs(params.row.createdAt).format("DD-MM-YYYY")}</p>
      ),
    },
  ];

  const handleCellClick = (params) => {
    if (params.field === "id" || params.field === "clientName") {
      navigate(`/workBoard/clients/view/${params.row.id}`);
    }
  };

  return (
    <>
      <BaseCard elevation={0} className="py-4 px-5 rounded-xl shadow-lg ">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 w-full">
          <div className="w-full sm:w-2/6">
            <BaseTextField
              id="customerEntitiesSearch"
              name="customerEntitiesSearch"
              type="search"
              autoComplete="off"
              placeholder="Search by name, email or ID"
              onChange={(e) => debouncedSearchRef.current(e.target.value)}
              startAdornment={<i className="fa-light fa-magnifying-glass text-lg"></i>}
            />
          </div>

          {isOwner && (
            <BaseButton
              color="primary"
              className="!py-[5px] whitespace-nowrap w-full sm:w-auto"
              onClick={() => {
                // Adding entities waits on approval, like adding users does.
                if (userData?.status !== "APPROVED") {
                  setIsProfileCompleted(true);
                  return;
                }
                navigate("/customerSettings/entities/create");
              }}
            >
              Add Entity
            </BaseButton>
          )}
        </div>
      </BaseCard>

      <BaseDataTableV2
        className="max-h-[calc(100vh-320px)] overflow-auto"
        rowHeight={38}
        columns={columns}
        data={entities}
        isLoading={isPending}
        onCellClick={handleCellClick}
        disableColumnResize={false}
        disableRowSelectionOnClick
        hideFooter={false}
        pagination
        rowCount={totalCount}
        pageSizeOptions={[10, 20, 50, 100]}
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        paginationMode="server"
      />

      {isProfileCompleted && (
        <CompleteProfileModal
          action="add an entity"
          submittedMessage="Sorry! You can add your entities after your profile verification."
          isProfileCompleted={isProfileCompleted}
          setIsProfileCompleted={setIsProfileCompleted}
          status={userData?.status}
          authRole={authRole}
        />
      )}
    </>
  );
};

export default EntitiesTable;
